import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Modal, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { GLOBAL_PRODUCT_ID_UPDATE } from 'lib/networking/endpoints';
import RadioButton from 'components/shared/RadioButton';
import SwalDialog from 'lib/utils/toast';
import usePost from 'lib/hooks/usePost';

function GlobalProductUpdateModal({ onHide, globalProduct, newGlobalProductId }) {
  const [selectedIds, setSelectedIds] = useState(() => {
    const distributorProducts = globalProduct.global_product_vendors || [];
    const manufacturerProducts = globalProduct.global_product_manufacturers || [];
    return {
      distributorProducts: distributorProducts.map(product => product.id),
      manufacturerProducts: manufacturerProducts.map(product => product.id),
    };
  });

  const { postData: updateGlobalProductId } = usePost(
    GLOBAL_PRODUCT_ID_UPDATE,
    () => {
      SwalDialog('success', 'Global product id updated.', 'Success', 'center');
      onHide();
      window.location.reload();
    },
    () => {
      SwalDialog('error', 'An error occurred during global product id update.', 'Error', 'center');
    },
  );

  const distributorProducts = globalProduct.global_product_vendors || [];
  const manufacturerProducts = globalProduct.global_product_manufacturers || [];

  const tableHeaders = (
    <thead>
      <tr>
        <th></th>
        <th>ID</th>
        <th>SKU</th>
        <th>Name</th>
      </tr>
    </thead>
  );

  return (
    <Modal show={true} onHide={onHide} size="lg" className="px-6" centered>
      <Modal.Header closeButton>
        <div className="d-flex flex-column">
          <Modal.Title className="mont">
            <h2 className="font-weight-bold">Update Global Product ID For all below products</h2>
          </Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-1">
          <Col>
            <div className={css(styles.heading)}>Manufacturer</div>
          </Col>
        </Row>
        <Table hover>
          {tableHeaders}
          <tbody>
            {manufacturerProducts.map(product => (
              <tr key={product.id}>
                <td>
                  <RadioButton
                    box
                    className="align-middle"
                    small={true}
                    checked={selectedIds.manufacturerProducts?.includes(product.id)}
                    onClick={() => {
                      const newSelected = selectedIds.manufacturerProducts.includes(product.id)
                        ? selectedIds.manufacturerProducts.filter(id => id !== product.id)
                        : [...selectedIds.manufacturerProducts, product.id];
                      setSelectedIds({
                        ...selectedIds,
                        manufacturerProducts: newSelected,
                      });
                    }}
                  />
                </td>
                <td>{product.id}</td>
                <td>{product.manufacturer_sku}</td>
                <td>{product.name}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Row>
          <Col>
            <div className={css(styles.heading)}>Distributors</div>
          </Col>
        </Row>
        <Table hover>
          {tableHeaders}
          <tbody>
            {distributorProducts.map(product => (
              <tr key={product.id}>
                <td>
                  <RadioButton
                    box
                    className="align-middle"
                    small={true}
                    checked={selectedIds.distributorProducts?.includes(product.id)}
                    onClick={() => {
                      const newSelected = selectedIds.distributorProducts.includes(product.id)
                        ? selectedIds.distributorProducts.filter(id => id !== product.id)
                        : [...selectedIds.distributorProducts, product.id];
                      setSelectedIds({
                        ...selectedIds,
                        distributorProducts: newSelected,
                      });
                    }}
                  />
                </td>
                <td>{product.id}</td>
                <td>{product.vendor_sku}</td>
                <td>{product.name}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Row className="pt-3 mt-3">
          <Col sm={3}>
            <Button
              block
              onClick={() =>
                updateGlobalProductId({
                  global_product_id: newGlobalProductId,
                  global_product_vendors: selectedIds.distributorProducts,
                  global_product_manufacturers: selectedIds.manufacturerProducts,
                })
              }
            >
              Update
            </Button>
          </Col>
          <Col sm={2} className="pl-0">
            <Button variant="outline-primary" onClick={() => onHide()}>
              Cancel
            </Button>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

const styles = StyleSheet.create({
  heading: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
  },
});

GlobalProductUpdateModal.propTypes = {
  onHide: PropTypes.func,
  globalProduct: PropTypes.object.isRequired,
  newGlobalProductId: PropTypes.number.isRequired,
};

export default GlobalProductUpdateModal;
